import React, { useState, useEffect } from 'react';
import b2bService from '../../services/b2bService';
import './B2bPortalOrderHistoryPage.css';

const B2bPortalLedgerStatementPage = () => {
  const [statement, setStatement] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unbilled, setUnbilled] = useState(0);
  const [unappliedAdvance, setUnappliedAdvance] = useState(0);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });

  const fetchStatement = async () => {
    setLoading(true);
    try {
      const data = await b2bService.getPortalLedgerStatement({ fromDate, toDate });
      setStatement(Array.isArray(data) ? data : []);
      const m = await b2bService.getPortalDashboard().catch(() => null);
      setUnbilled(Number(m?.unbilledAmount ?? m?.UnbilledAmount ?? 0));
      setUnappliedAdvance(Number(m?.unappliedAdvances ?? m?.UnappliedAdvances ?? 0));
    } catch (err) {
      console.error('Error fetching ledger statement:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatement();
  }, [fromDate, toDate]);

  let runningBalance = 0;
  const computedStatement = statement.map((entry) => {
    const debit = Number(entry.debitAmount ?? entry.DebitAmount ?? 0);
    const credit = Number(entry.creditAmount ?? entry.CreditAmount ?? 0);
    runningBalance += debit - credit;
    return {
      ...entry,
      debit,
      credit,
      runningBalance
    };
  });

  const totalDebits = computedStatement.reduce((sum, e) => sum + e.debit, 0);
  const totalCredits = computedStatement.reduce((sum, e) => sum + e.credit, 0);
  const netClosingBalance = runningBalance;

  return (
    <div className="b2b-orders-container">
      {/* Header */}
      <div className="b2b-dashboard-header">
        <div>
          <h2 className="b2b-dashboard-title">
            <span style={{ fontSize: '24px' }}>📊</span> Financial Ledger &amp; Running Passbook
          </h2>
          <div className="b2b-dashboard-subtitle">
            Itemized audit trail of debits (contractual invoices) and credits (advances &amp; remittances) with live running balance.
          </div>
        </div>
        <button className="b2b-refresh-btn" onClick={() => window.print()}>
          <span>🖨️</span> Print Official Statement
        </button>
      </div>

      {/* Real-time Financial Transparency & Surplus Note */}
      {unappliedAdvance > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%)',
          border: '1px solid #a7f3d0',
          borderRadius: '10px',
          padding: '14px 18px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <span style={{ fontSize: '24px' }}>🛡️</span>
            <div>
              <div style={{ fontWeight: 700, color: '#065f46', fontSize: '0.92rem' }}>
                Surplus Advance Available: ₹{unappliedAdvance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: '0.82rem', color: '#047857', marginTop: '2px' }}>
                You have deposited extra advance quota. This surplus is safely held in your facility's wallet and will automatically cover future tests and invoices with zero deductions.
              </div>
            </div>
          </div>
          <span style={{
            background: '#059669',
            color: '#ffffff',
            padding: '4px 10px',
            borderRadius: '999px',
            fontSize: '0.75rem',
            fontWeight: 700,
            whiteSpace: 'nowrap'
          }}>
            ✓ Quota Active
          </span>
        </div>
      )}

      {unbilled > 0 && (
        <div style={{
          background: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '10px',
          padding: '12px 18px',
          marginBottom: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '20px' }}>⏱️</span>
            <div>
              <span style={{ fontWeight: 700, color: '#334155', fontSize: '0.88rem' }}>
                Unbilled Work In Progress: ₹{unbilled.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
              <span style={{ fontSize: '0.82rem', color: '#64748b', marginLeft: '8px' }}>
                (Tests processed during this cycle that have not yet been consolidated into a monthly invoice).
              </span>
            </div>
          </div>
          <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>
            Next Invoice: Cycle Close
          </span>
        </div>
      )}

      {/* Ledger KPI Cards */}
      <div className="b2b-orders-kpi-grid">
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Invoiced Debits</div>
          <div className="b2b-kpi-value" style={{ color: '#0f172a' }}>
            ₹{totalDebits.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Total billed charges</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Remitted Credits</div>
          <div className="b2b-kpi-value" style={{ color: '#059669' }}>
            ₹{totalCredits.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Advances &amp; payment receipts</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Net Passbook Balance</div>
          <div className="b2b-kpi-value" style={{ color: netClosingBalance > 0 ? '#dc2626' : '#059669' }}>
            ₹{Math.abs(netClosingBalance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            <span style={{ fontSize: '13px', fontWeight: '600', marginLeft: '6px' }}>
              {netClosingBalance > 0 ? 'DR (Due)' : netClosingBalance < 0 ? 'CR (Surplus)' : 'NIL'}
            </span>
          </div>
          <div className="b2b-kpi-sub">
            {netClosingBalance > 0 ? 'Payment required' : netClosingBalance < 0 ? 'Surplus in your credit' : 'Fully settled'}
          </div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Available Surplus Deposit</div>
          <div className="b2b-kpi-value" style={{ color: unappliedAdvance > 0 ? '#059669' : '#64748b' }}>
            ₹{unappliedAdvance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Live unallocated wallet balance</div>
        </div>
      </div>

      {/* Date Filter Bar */}
      <div className="b2b-filter-bar">
        <div className="b2b-filter-group">
          <label className="b2b-filter-label">Statement Period From</label>
          <input
            type="date"
            className="b2b-filter-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="b2b-filter-group">
          <label className="b2b-filter-label">Statement Period To</label>
          <input
            type="date"
            className="b2b-filter-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <button className="b2b-filter-submit-btn" onClick={fetchStatement}>
          🔍 Refresh Statement
        </button>
      </div>

      {/* Statement Table Card */}
      <div className="b2b-table-card">
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            Loading authenticated ledger entries...
          </div>
        ) : statement.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📂</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
              No financial transactions recorded in the specified date range.
            </div>
            <div style={{ fontSize: '13px', marginTop: '6px' }}>
              All invoices and advance top-ups automatically post here in real time.
            </div>
          </div>
        ) : (
          <table className="b2b-data-table">
            <thead>
              <tr>
                <th>Voucher Date</th>
                <th>Entry Type</th>
                <th>Reference Number</th>
                <th>Notes / Audit Narrative</th>
                <th style={{ textAlign: 'right' }}>Debit / Billed (₹)</th>
                <th style={{ textAlign: 'right' }}>Credit / Paid (₹)</th>
                <th style={{ textAlign: 'right' }}>Running Balance (₹)</th>
              </tr>
            </thead>
            <tbody>
              {computedStatement.map((s, idx) => {
                const date = s.transactionDate || s.TransactionDate;
                const type = (s.entryType || s.EntryType || '').toUpperCase();
                const ref = s.referenceNumber || s.ReferenceNumber;
                const notes = s.notes || s.Notes;

                return (
                  <tr key={idx}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '600', color: '#0f172a' }}>
                        {date ? new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '--'}
                      </div>
                    </td>
                    <td>
                      <span className={`b2b-status-tag ${type.includes('INVOICE') ? 'breached' : 'authorized'}`}>
                        {type.includes('INVOICE') ? '📑 INVOICE DEBIT' : '💳 ADVANCE CREDIT'}
                      </span>
                    </td>
                    <td>
                      <span className="b2b-ord-tag">{ref}</span>
                    </td>
                    <td style={{ fontSize: '12px', color: '#475569', maxWidth: '320px' }}>
                      {notes || 'Automated billing ledger entry'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: s.debit > 0 ? '#dc2626' : '#94a3b8' }}>
                      {s.debit > 0 ? `₹${s.debit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '700', color: s.credit > 0 ? '#059669' : '#94a3b8' }}>
                      {s.credit > 0 ? `₹${s.credit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}` : '—'}
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '800', color: s.runningBalance > 0 ? '#dc2626' : '#059669' }}>
                      ₹{Math.abs(s.runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      <span style={{ fontSize: '10px', marginLeft: '4px', color: '#64748b' }}>
                        {s.runningBalance > 0 ? 'DR' : s.runningBalance < 0 ? 'CR' : ''}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default B2bPortalLedgerStatementPage;
